import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isUserAuthorizedByServer } from "../api/userAccess";
import DailyTORReport from "../components/DailyTORReport";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import PageLoading from "../components/PageLoading";
import TimeOffDetailReport from "../components/TimeOffDetailReport";
import StarsConflictReport from "../components/StarsConflictReport";
import PermanentBidReport from "../components/PermanentBidReport";
import TradeDailyReport from "../components/TradeDailyReport";
import TradeDetailReport from "../components/TradeDetailReport";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import { getAdminStation, getMultiStationAdminStationList } from "../util/commonUtils";
import {
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  DAILY_TIME_OFF_REQUEST_REPORT,
  DAILY_TRADE_REPORT,
  HOME_PATH,
  TIME_OFF_REQUEST_DETAIL_REPORT,
  TRADE_DETAIL_REPORT,
  SCHEDULE_CONFLICT_REPORT,
  PERMANENT_BID_REPORT,
} from "../util/constants";
import { validateStation } from "../util/inputValidation";
import { ADMIN_MOBILE_WARNING_MESSAGE, REPORT_TYPE_IS_REQUIRED } from "../util/messageConstants";

const useStyles = makeStyles((theme) => ({
  root: {
    textAlign: "start",
    height: "100%",
    overflowY: "auto",
  },

  title: {
    fontSize: "24px",
    maxWidth: "90vw",
    marginTop: "10px",
  },
  control: {
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  reportMain: {
    margin: "0 auto",
    padding: "16px",
    marginBottom: "100px",
  },
}));

const GenerateReportPage = (props) => {
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const [selectedStation, setSelectedStation] = useState(false);
  const [selectedStationErrors, setSelectedStationErrors] = useState("");
  const [adminStations, setAdminStations] = useState([]);
  const [reportType, setReportType] = useState("");
  const [reportTypeErrors, setReportTypeErrors] = useState("");
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      const station = getAdminStation(props.featureConfig.userStations, userSession, stationFilter);
      setSelectedStation(station);
      setSelectedStationErrors(validateStation(station));
      setAdminStations(getMultiStationAdminStationList(userSession));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const isFormValidationSuccess = () => {
    return selectedStationErrors === "" && reportTypeErrors === "";
  };

  const resetState = () => {
    setReportType("");
    setReportTypeErrors("");
  };

  const handleReportTypeSelectChange = (event) => {
    setReportType(event.target.value);
    setReportTypeErrors("");
  };

  const handleReportTypeSelectBlur = (event) => {
    if (event.target.value === "") setReportTypeErrors(REPORT_TYPE_IS_REQUIRED);
  };

  const handleStationChange = (event) => {
    setSelectedStation(event.target.value);
    setSelectedStationErrors(validateStation(event.target.value));
    dispatch(updateStationFilter(event.target.value));
  };

  const renderStations = () => {
    return adminStations
      .slice()
      .sort()
      .map((station) => {
        return (
          <MenuItem key={station} value={station} disabled={!props.featureConfig.userStations.includes(station)}>
            {station}
          </MenuItem>
        );
      });
  };

  const renderReportTypeContent = () => {
    if (isFormValidationSuccess()) {
      switch (reportType) {
        case DAILY_TRADE_REPORT:
          return <TradeDailyReport station={selectedStation} onResetState={resetState} />;
        case TRADE_DETAIL_REPORT:
          return <TradeDetailReport station={selectedStation} onResetState={resetState} />;
        case DAILY_TIME_OFF_REQUEST_REPORT:
          return <DailyTORReport station={selectedStation} onResetState={resetState} />;
        case TIME_OFF_REQUEST_DETAIL_REPORT:
          return <TimeOffDetailReport station={selectedStation} onResetState={resetState} />;
        case SCHEDULE_CONFLICT_REPORT:
          return <StarsConflictReport station={selectedStation} onResetState={resetState} />;
        case PERMANENT_BID_REPORT:
          return <PermanentBidReport station={selectedStation} onResetState={resetState} />;
        default:
          return "";
      }
    }
    return "";
  };

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.GENERATE_REPORT];
  const breadcrumbsLinks = [HOME_PATH];

  const renderReportTypes = () => {
    return props.featureConfig.reports
      .slice()
      .sort()
      .map((reportType) => {
        return (
          <MenuItem key={reportType} value={reportType}>
            {reportType}
          </MenuItem>
        );
      });
  };
  const confirmationRequired = () => {
    if (reportType !== "") {
      return true;
    } else {
      return false;
    }
  };
  const renderMobileGenerateReportPage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  const renderDesktopGenerateReportPage = () => {
    return (
      <>
        <StarsBreadcrumbs
          breadcrumbsList={breadcrumbsList}
          breadcrumbsLinks={breadcrumbsLinks}
          isConfirmationRequired={confirmationRequired()}
          cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.SELECTIONS}
        />
        <Grid container className={classes.root}>
          <Grid item className={classes.reportMain}>
            <Grid item xs={12}>
              <Box className={classes.title}>Generate Report</Box>
            </Grid>
            <Grid container>
              <Grid item>
                <FormControl variant="outlined" className={classes.control} error={reportTypeErrors !== ""}>
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    className="select-field"
                    value={reportType}
                    onChange={handleReportTypeSelectChange}
                    onBlur={handleReportTypeSelectBlur}
                    label="Report Type"
                  >
                    <MenuItem value="" disabled>
                      <em>Report Type</em>
                    </MenuItem>
                    {renderReportTypes()}
                  </Select>
                  {reportTypeErrors !== "" && <FormHelperText error>{reportTypeErrors}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl variant="outlined" className={classes.control} error={selectedStationErrors !== ""}>
                  <InputLabel>Station</InputLabel>
                  <Select
                    className="select-field"
                    value={selectedStation}
                    onChange={handleStationChange}
                    label="Station"
                  >
                    <MenuItem value="" disabled>
                      <em>Station</em>
                    </MenuItem>
                    {renderStations()}
                  </Select>
                  {selectedStationErrors !== "" && <FormHelperText error>{selectedStationErrors}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
            {renderReportTypeContent()}
          </Grid>
        </Grid>
      </>
    );
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <Grid data-testid="generate-report-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopGenerateReportPage() : renderMobileGenerateReportPage()}</>
      )}
    </Grid>
  );
};

export default GenerateReportPage;
